// Make sure that there is at least one default selected member for each parameter group. This is so that Highcharts requires at least one series in left axis.
import {
  convertMetresPerSecondTo,
  convertMetresTo,
  convertRadiansToDegrees
} from '../../../Flying/Map/utils/helper';
import { DisplayPoint } from '../../../../common/api/spidertracks-sdk/types/TrackData';
import { UserData } from '../../../../redux/types';
import { FlightDataSample, SupplementarySample } from '../../../../types/FlightData';

const DP = 2;

export interface Parameter {
  key: string;
  name: string;
  displayName: string;
  tooltip?: string;
  prerequisites?: {
    AHRS?: boolean;
    featureFlag?: string;
    flightDataSample?: (sample: FlightDataSample) => boolean;
    supplementaryData?: (sample: SupplementarySample) => boolean;
  };
  accessors: {
    flightDataSample?: (sample: FlightDataSample, userData: UserData) => number | null;
    displayPoint?: (sample: DisplayPoint, userData: UserData) => number;
    supplementaryData?: (sample: SupplementarySample, userData: UserData) => number | null;
  };
}

export interface ParameterGroup {
  key: string;
  name: string;
  members: Parameter[];
  unit: (userData: UserData) => string;
  defaultSelectedMemberNames: string[];
  featureFlag?: string;
  axisDisplayString: (userData: UserData) => string;
}

export const AvailableChartParameters: ParameterGroup[] = [
  {
    key: 'altitude',
    name: 'Altitude',
    defaultSelectedMemberNames: ['AMSL'],
    axisDisplayString: (userData: UserData) => `Altitude (${userData.altitudeUnit})`,
    unit: (userData: UserData) => userData.altitudeUnit,
    members: [
      {
        key: 'amsl',
        name: 'AMSL',
        displayName: 'Altitude AMSL',
        tooltip: 'Height Above Mean Sea Level',
        accessors: {
          flightDataSample: (sample, userData) =>
            convertMetresTo(sample.amslMetres, userData.altitudeUnit, DP)[0],
          displayPoint: (sample: DisplayPoint, userData: UserData) => {
            if (sample.altitudeUnit === 'm') {
              return convertMetresTo(sample.altitude, userData.altitudeUnit, DP)[0];
            } else {
              return parseFloat(sample.altitude.toFixed(DP));
            }
          }
        }
      },
      {
        key: 'agl',
        name: 'AGL',
        displayName: 'Altitude AGL',
        tooltip: 'Height Above Ground Level',
        prerequisites: {
          flightDataSample: (sample: FlightDataSample) => true
        },
        accessors: {
          flightDataSample: (sample, userData) =>
            convertMetresTo(sample.aglMetres, userData.altitudeUnit, DP)[0]
        }
      },
      {
        key: 'terrain',
        name: 'Terrain',
        displayName: 'Terrain',
        prerequisites: {
          flightDataSample: (sample: FlightDataSample) => true
        },
        accessors: {
          flightDataSample: (sample, userData) =>
            convertMetresTo(sample.elevationMetres, userData.altitudeUnit, DP)[0]
        }
      }
    ]
  },
  {
    key: 'speed',
    name: 'Speed',
    defaultSelectedMemberNames: ['Ground Speed'],
    axisDisplayString: (userData: UserData) => `Speed (${userData.speedUnit})`,
    unit: (userData: UserData) => userData.speedUnit,
    members: [
      {
        key: 'groundSpeed',
        name: 'Ground Speed',
        displayName: 'Ground Speed',
        tooltip: 'From GPS',
        accessors: {
          flightDataSample: (sample, userData) =>
            convertMetresPerSecondTo(sample.groundSpeedMetresPerSecond, userData.speedUnit, DP),
          displayPoint: (sample: DisplayPoint, userData: UserData) => {
            if (sample.speed_raw !== undefined) {
              return convertMetresPerSecondTo(sample.speed_raw.value, userData.speedUnit, DP);
            }
            return convertMetresPerSecondTo(sample.speed, userData.speedUnit, DP);
          }
        }
      },
      {
        key: 'ias',
        name: 'Indicated Air Speed',
        displayName: 'Indicated Air Speed',
        tooltip: '',
        prerequisites: {
          supplementaryData: (sample: SupplementarySample) =>
            sample != undefined && sample.indicatedAirSpeedMetresPerSecond != undefined
        },
        accessors: {
          supplementaryData: (sample, userData) =>
            sample.indicatedAirSpeedMetresPerSecond == undefined
              ? null
              : convertMetresPerSecondTo(
                  sample.indicatedAirSpeedMetresPerSecond,
                  userData.speedUnit,
                  DP
                )
        }
      }
    ]
  },
  {
    key: 'vertical_speed',
    name: 'Vertical Speed',
    defaultSelectedMemberNames: ['Vertical Speed'],
    axisDisplayString: (userData: UserData) => `Vertical Speed (ft/min)`,
    unit: (userData: UserData) => 'ft/min',
    members: [
      {
        key: 'verticalSpeed',
        name: 'Vertical Speed',
        displayName: 'Vertical Speed',
        tooltip: 'From GPS',
        prerequisites: {
          AHRS: true,
          flightDataSample: (sample: FlightDataSample) =>
            !(sample.velocity?.downMetresPerSecond === undefined)
        },
        accessors: {
          flightDataSample: (sample, userData) =>
            convertMetresPerSecondTo(sample.velocity.downMetresPerSecond * -1.0, 'ft/min', DP) // Make it so that up is +ve)
        }
      }
    ]
  },
  {
    key: 'attitude',
    name: 'Attitude',
    defaultSelectedMemberNames: ['Pitch'],
    axisDisplayString: (userData: UserData) => `Attitude (degrees)`,
    unit: (userData: UserData) => 'degrees',
    members: [
      {
        key: 'pitch',
        name: 'Pitch',
        displayName: 'Pitch',
        prerequisites: {
          AHRS: true,
          flightDataSample: (sample: FlightDataSample) => !(sample.pitchRadians === undefined)
        },
        accessors: {
          flightDataSample: (sample, userData) => convertRadiansToDegrees(sample.pitchRadians)
        }
      },
      {
        key: 'roll',
        name: 'Roll',
        displayName: 'Roll',
        prerequisites: {
          AHRS: true,
          flightDataSample: (sample: FlightDataSample) => !(sample.rollRadians === undefined)
        },
        accessors: {
          flightDataSample: (sample, userData) => convertRadiansToDegrees(sample.rollRadians)
        }
      }
    ]
  },
  // TODO update when g-force is available
  {
    key: 'forceG',
    name: 'Force',
    defaultSelectedMemberNames: ['Peak'],
    featureFlag: 'enable-peak-g',
    axisDisplayString: (userData: UserData) => `Force (g)`,
    unit: (userData: UserData) => 'g',
    members: [
      {
        key: 'peak',
        name: 'Peak',
        displayName: 'Peak',
        tooltip: 'Magnitude (all axes; includes gravity)',
        prerequisites: {
          AHRS: true,
          flightDataSample: (sample: FlightDataSample) => !(sample.gForce.peak === undefined)
        },
        accessors: {
          flightDataSample: (sample, userData) =>
            sample.gForce.peak === undefined ? null : parseFloat(sample.gForce.peak!.toFixed(DP))
        }
      },
      {
        key: 'lateral',
        name: 'Lateral',
        displayName: 'Lateral',
        prerequisites: {
          AHRS: true,
          flightDataSample: (sample: FlightDataSample) => !(sample.gForce.lateral === undefined)
        },
        accessors: {
          flightDataSample: (sample, userData) =>
            sample.gForce.lateral === undefined
              ? null
              : parseFloat(sample.gForce.lateral!.toFixed(DP))
        }
      },
      {
        key: 'normal',
        name: 'Normal',
        displayName: 'Normal',
        prerequisites: {
          AHRS: true,
          flightDataSample: (sample: FlightDataSample) => !(sample.gForce.normal === undefined)
        },
        accessors: {
          flightDataSample: (sample, userData) =>
            sample.gForce.normal === undefined
              ? null
              : parseFloat(sample.gForce.normal!.toFixed(DP))
        }
      }
    ]
  },
  {
    key: 'temperature',
    name: 'Temperature',
    defaultSelectedMemberNames: [],
    axisDisplayString: (userData: UserData) => `Temp (°C)`,
    unit: (userData: UserData) => '°C',
    members: [
      {
        key: 'engine1cht1',
        name: 'Engine 1 CHT1',
        displayName: 'Engine 1 CHT1',
        tooltip: 'Cylinder head temperature',
        prerequisites: {
          supplementaryData: (sample: SupplementarySample) =>
            sample != undefined && sample.engine1Cht1DegreesC != undefined
        },
        accessors: {
          supplementaryData: (sample, userData) =>
            sample.engine1Cht1DegreesC === undefined
              ? null
              : parseFloat(sample.engine1Cht1DegreesC.toFixed(DP))
        }
      },
      {
        key: 'engine1ittdegreesc',
        name: 'Engine 1 ITT',
        displayName: 'Engine 1 ITT',
        tooltip: 'Inter turbine temperature',
        prerequisites: {
          supplementaryData: (sample: SupplementarySample) =>
            sample != undefined && sample.engine1IttDegreesC != undefined
        },
        accessors: {
          supplementaryData: (sample, userData) =>
            sample.engine1IttDegreesC === undefined
              ? null
              : parseFloat(sample.engine1IttDegreesC.toFixed(DP))
        }
      },
      {
        key: 'outsideairtemperaturedegreesc',
        name: 'Outside air temperature',
        displayName: 'Outside air temperature',
        tooltip: 'Outside air temperature',
        prerequisites: {
          supplementaryData: (sample: SupplementarySample) =>
            sample != undefined && sample.outsideAirTemperatureDegreesC != undefined
        },
        accessors: {
          supplementaryData: (sample, userData) =>
            sample.outsideAirTemperatureDegreesC === undefined
              ? null
              : parseFloat(sample.outsideAirTemperatureDegreesC.toFixed(DP))
        }
      },
      {
        key: 'engine1egt1',
        name: 'Engine 1 EGT1',
        displayName: 'Engine 1 EGT1',
        tooltip: 'Exhaust gas temperature',
        prerequisites: {
          supplementaryData: (sample: SupplementarySample) =>
            sample != undefined && sample.engine1Egt1DegreesC != undefined
        },
        accessors: {
          supplementaryData: (sample, userData) =>
            sample.engine1Egt1DegreesC === undefined
              ? null
              : parseFloat(sample.engine1Egt1DegreesC.toFixed(DP))
        }
      },
      {
        key: 'engine1mgt1',
        name: 'Engine 1 MGT1',
        displayName: 'Engine 1 MGT1',
        tooltip: 'Measured gas temperature',
        prerequisites: {
          supplementaryData: (sample: SupplementarySample) =>
            sample != undefined && sample.engine1MgtDegreesC != undefined
        },
        accessors: {
          supplementaryData: (sample, userData) =>
            sample.engine1MgtDegreesC === undefined
              ? null
              : parseFloat(sample.engine1MgtDegreesC.toFixed(DP))
        }
      },
      {
        key: 'engine1oil',
        name: 'Engine 1 Oil',
        displayName: 'Engine 1 Oil',
        tooltip: 'Oil temperature',
        prerequisites: {
          supplementaryData: (sample: SupplementarySample) =>
            sample != undefined && sample.engine1OilTDegreesC != undefined
        },
        accessors: {
          supplementaryData: (sample, userData) =>
            sample.engine1OilTDegreesC === undefined
              ? null
              : parseFloat(sample.engine1OilTDegreesC.toFixed(DP))
        }
      }
    ]
  },
  {
    key: 'rotationalspeed',
    name: 'Rotational speed',
    defaultSelectedMemberNames: [],
    axisDisplayString: (userData: UserData) => `Rotational speed (rpm)`,
    unit: (userData: UserData) => 'rpm',
    members: [
      {
        key: 'engine1rpm',
        name: 'Engine 1 Speed (RPM)',
        displayName: 'Engine 1 speed (rpm)',
        tooltip: 'Engine 1 speed (rpm)',
        prerequisites: {
          supplementaryData: (sample: SupplementarySample) =>
            sample != undefined && sample.engine1Rpm != undefined
        },
        accessors: {
          supplementaryData: (sample, userData) =>
            sample.engine1Rpm === undefined ? null : parseFloat(sample.engine1Rpm.toFixed(DP))
        }
      },
      {
        key: 'n1Rpm',
        name: 'N1 Speed (RPM)',
        displayName: 'N1 Speed (RPM)',
        tooltip: 'Low Pressure Compressor Speed (RPM)',
        prerequisites: {
          supplementaryData: (sample: SupplementarySample) =>
            sample != undefined && sample.n1Rpm != undefined
        },
        accessors: {
          supplementaryData: (sample, userData) =>
            sample.n1Rpm === undefined ? null : parseFloat(sample.n1Rpm.toFixed(DP))
        }
      },
      {
        key: 'n2Rpm',
        name: 'N2 Speed (RPM)',
        displayName: 'N2 Speed (RPM)',
        tooltip: 'High Pressure Compressor Speed (RPM)',
        prerequisites: {
          supplementaryData: (sample: SupplementarySample) =>
            sample != undefined && sample.n2Rpm != undefined
        },
        accessors: {
          supplementaryData: (sample, userData) =>
            sample.n2Rpm === undefined ? null : parseFloat(sample.n2Rpm.toFixed(DP))
        }
      },
      {
        key: 'rotorSpeed',
        name: 'Rotor Speed (RPM)',
        displayName: 'Rotor Speed (RPM)',
        tooltip: 'Rotor Speed (RPM)',
        prerequisites: {
          supplementaryData: (sample: SupplementarySample) =>
            sample != undefined && sample.rotorSpeedRpm != undefined
        },
        accessors: {
          supplementaryData: (sample, userData) =>
            sample.rotorSpeedRpm === undefined ? null : parseFloat(sample.rotorSpeedRpm.toFixed(DP))
        }
      }
    ]
  },
  {
    key: 'pressure',
    name: 'Pressure',
    defaultSelectedMemberNames: [],
    axisDisplayString: (userData: UserData) => `Pressure (Pa)`,
    unit: (userData: UserData) => 'Pa',
    members: [
      {
        key: 'engine1map',
        name: 'Engine 1 MAP',
        displayName: 'Engine 1 MAP',
        tooltip: 'Inlet manifold air pressure',
        prerequisites: {
          supplementaryData: (sample: SupplementarySample) =>
            sample != undefined && sample.engine1MapPa != undefined
        },
        accessors: {
          supplementaryData: (sample, userData) =>
            sample.engine1MapPa === undefined ? null : parseFloat(sample.engine1MapPa.toFixed(DP))
        }
      },
      {
        key: 'engine1oil',
        name: 'Engine 1 oil',
        displayName: 'Engine 1 oil',
        tooltip: 'Engine 1 oil pressure',
        prerequisites: {
          supplementaryData: (sample: SupplementarySample) =>
            sample != undefined && sample.engine1OilPPa != undefined
        },
        accessors: {
          supplementaryData: (sample, userData) =>
            sample.engine1OilPPa === undefined ? null : parseFloat(sample.engine1OilPPa.toFixed(DP))
        }
      }
    ]
  },
  {
    key: 'Range',
    name: 'Range',
    defaultSelectedMemberNames: [],
    axisDisplayString: (userData: UserData) => `Range (%)`,
    unit: (userData: UserData) => '%',
    members: [
      {
        key: 'torquePercent',
        name: 'Engine 1 Torque',
        displayName: 'Engine 1 Torque',
        tooltip: 'Torque (%)',
        prerequisites: {
          AHRS: true,
          supplementaryData: (sample: SupplementarySample) =>
            sample != undefined && sample.engine1TqPct != undefined
        },
        accessors: {
          supplementaryData: (sample, userData) =>
            sample.engine1TqPct === undefined ? null : parseFloat(sample.engine1TqPct.toFixed(DP))
        }
      },
      {
        key: 'n1RpmPercent',
        name: 'N1 RPM',
        displayName: 'N1 RPM',
        tooltip: 'Low Pressure Compresser RPM (%)',
        prerequisites: {
          AHRS: true,
          supplementaryData: (sample: SupplementarySample) =>
            sample != undefined && sample.n1RpmPct != undefined
        },
        accessors: {
          supplementaryData: (sample, userData) =>
            sample.n1RpmPct === undefined ? null : parseFloat(sample.n1RpmPct.toFixed(DP))
        }
      },
      {
        key: 'n2RpmPercent',
        name: 'N2 RPM',
        displayName: 'N2 RPM',
        tooltip: 'High Pressure Compresser RPM (%)',
        prerequisites: {
          AHRS: true,
          supplementaryData: (sample: SupplementarySample) =>
            sample != undefined && sample.n2RpmPct != undefined
        },
        accessors: {
          supplementaryData: (sample, userData) =>
            sample.n2RpmPct === undefined ? null : parseFloat(sample.n2RpmPct.toFixed(DP))
        }
      },
      {
        key: 'rotorSpeedPercent',
        name: 'Rotor Speed',
        displayName: 'Rotor Speed',
        tooltip: 'Rotor Speed (%)',
        prerequisites: {
          AHRS: true,
          supplementaryData: (sample: SupplementarySample) =>
            sample != undefined && sample.rotorSpeedPct != undefined
        },
        accessors: {
          supplementaryData: (sample, userData) =>
            sample.rotorSpeedPct === undefined ? null : parseFloat(sample.rotorSpeedPct.toFixed(DP))
        }
      }
    ]
  }
];
